import axios from 'axios';
import { assocPath } from 'ramda';
import * as Config from '../../config/config';
import { LocalStorage, flattenObjectToDotNotation, retrieveUriParam } from '../../utils/helpers';

/**
 * Object::Promise
 * Takes an object containing clientId and redirectUri
 * returns a promise resolving to data provided by the clients endpoint
 *
 * @param {Object} params
 */
export const validateClient = ({ clientId, redirectUri }) => {
  const invalidClientErr = new Error('Invalid client_id or redirect_uri');
  if (!clientId || !redirectUri) return Promise.reject(invalidClientErr.message);

  const options = {
    method: 'get',
    url: `${Config.AUTH_API_BASE_URL}/clients/${clientId}`,
    headers: {
      'X-API-KEY': Config.AUTH_API_KEY
    }
  };

  return new Promise((resolve, reject) => {
    axios(options)
      .then(res => {
        const { data: { endpoints } } = res;

        if (endpoints && endpoints.redirectUris) {
          if (endpoints.redirectUris.includes(redirectUri)) resolve(res.data);

          endpoints.redirectUris.filter(uri => uri.match(/\/*\//)).forEach(uri => {
            const midIndex = uri.indexOf('*');
            const firstHalfStr = uri.substr(0, midIndex);
            const secondHalfStr = uri.substr(midIndex + 1, uri.length);

            if (redirectUri.indexOf(firstHalfStr) > -1 && redirectUri.indexOf(secondHalfStr) > -1) {
              resolve(res.data);
            }
          });

          reject(invalidClientErr.message);
        } else {
          reject(invalidClientErr.message);
        }
      })
      .catch(() => {
        reject(invalidClientErr.message);
      });
  });
};

export const validateCode = () => {
  const missingCodeErr = new Error('Invalid client_id or redirect_uri');
  if (!retrieveUriParam('code')) return Promise.reject(missingCodeErr.message);
  return Promise.resolve();
};

/**
 * Array::Object
 * Takes an array of user emails
 * returns an object with email keys and first and last names as key-value pairs
 * from querying the API
 *
 * @param {Array} params
 */
export const fetchUsersInfo = async emails => {
  const promises = [];

  for (let i = 0; i < emails.length; i += 1) {
    const email = emails[i];
    const options = {
      method: 'get',
      url: `${Config.AUTH_API_BASE_URL}/users?email=${encodeURIComponent(email.toLowerCase())}`,
      headers: {
        'X-API-KEY': Config.AUTH_API_KEY
      }
    };
    promises.push(axios(options));
  }

  const responses = await Promise.all(promises);

  return responses.reduce((obj, res) => {
    if (res.data[0]) {
      return {
        ...obj,
        [res.data[0].email]: {
          firstName: res.data[0].firstName,
          lastName: res.data[0].lastName
        }
      };
    }
    return obj;
  }, {});
};

/**
 * Object<Spec>::Object
 * takes an object spec and a supplier, spec shows how the object should look.
 * Supplier will take the object as dot notation to return value
 * Returns a Traditional Object with values from supplier
 * supplier must implement a get method.
 * example
 * this:{
 *  is:{
 *    a:{
 *      spec: ''
 *    }
 *  }
 * }
 * supplier.get('this.is.a.spec')
 *
 *
 * @param {Object} spec spec in the form of dotnotationkey
 */
export const retrieveObject = (spec, supplier = LocalStorage) => {
  const objectAsDot = flattenObjectToDotNotation(spec);
  return Object.keys(objectAsDot).reduce(
    (acum, cK) => assocPath(cK.split('.'), supplier.get(cK), acum),
    {}
  );
};



// WEBPACK FOOTER //
// ./src/core/app/util.js